import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateVehicleTypeRequestSchema,
  type CreateVehicleTypeRequest,
  UpdateVehicleTypeRequestSchema,
  type UpdateVehicleTypeRequest,
  type VehicleTypeDetail,
  type VehicleTypeListItem,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateVehicleType, useUpdateVehicleType } from '../use-vehicle-types';

import { MasterCodeField } from '@/components/master-code-field';

interface VehicleTypeFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  vehicleType?: VehicleTypeDetail | VehicleTypeListItem | null;
}

const CREATE_DEFAULTS: CreateVehicleTypeRequest = {
  code: '',
  name: '',
  paxCapacity: null,
  sortOrder: 0,
  isActive: true,
};

const VehicleTypeFields = ({
  pending,
  isCreate,
}: {
  pending: boolean;
  isCreate: boolean;
}): React.JSX.Element => (
  <>
    <MasterCodeField isCreate={isCreate} required />
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Toyota Innova"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="paxCapacity">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Pax capacity"
          precision={0}
          min={1}
          max={100}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? null)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive vehicle types are hidden from itinerary dropdowns."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const VehicleTypeFormDrawer = ({
  open,
  onOpenChange,
  mode,
  vehicleType,
}: VehicleTypeFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createVehicleType = useCreateVehicleType();
  const updateVehicleType = useUpdateVehicleType();
  const pending = createVehicleType.isPending || updateVehicleType.isPending;

  const createForm = useForm<CreateVehicleTypeRequest>({
    resolver: zodResolver(CreateVehicleTypeRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateVehicleTypeRequest>({
    resolver: zodResolver(UpdateVehicleTypeRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (vehicleType) {
      updateForm.reset({
        code: vehicleType.code,
        name: vehicleType.name,
        paxCapacity: vehicleType.paxCapacity,
        sortOrder: vehicleType.sortOrder,
        isActive: vehicleType.isActive,
      });
    }
  }, [open, isCreate, vehicleType, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createVehicleType.mutateAsync(values);
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!vehicleType) return;
    await updateVehicleType.mutateAsync({ id: vehicleType.id, payload: values });
    onOpenChange(false);
  });

  const fields = <VehicleTypeFields pending={pending} isCreate={isCreate} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add vehicle type' : `Edit ${vehicleType?.name ?? 'vehicle type'}`}
      description="The code is the stable identifier (e.g. SEDAN). Pax capacity is optional but recommended for capacity-check warnings."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
